import * as React from 'react';
import { ReactElement, ReactNode } from 'react';
import { Block } from '../../components/layout/Block';
import { Props } from './props';
import { IntlShape, useIntl } from 'react-intl';
import { CheckPermissionFunction, PermissionGate } from '../../../../Auth/components/PermissionGate';
import { Panel } from '../../components/layout/Panel';
import { Actions, MultiInputControls } from '../../../components/form/MultiInput/MultiInputControls';
import { SubjectDTO, WorkDescriptionDTO, WorkKeywordDTO } from '../../../../api/core';
import { getDefaultWorkDescription, getDefaultWorkKeyword } from '../../../../Settings/emptyModels';
import { InputGroup } from '../../components/layout/InputGroup';
import { LabelledInputText } from '../../../components/form/LabelledInputText';
import { Subjects } from '../../components/parts/Subjects';
import { LabelledInputTextarea } from '../../../components/form/LabelledInputTextarea';
import { JustifiedDeleteButton } from '../../../components/form/MultiInput/Buttons';
import { LabelledDropdown } from '../../../components/form/LabelledDropdown';
import { getVocabularyOptions } from '../../../../Vocabulary/util';

export const ContentPage = (props: Props): ReactElement => {
  const intl: IntlShape = useIntl();
  return <Block title={`work.header.content`}>
    <PermissionGate entity={'work_generic'} field={'work_keywords'}>
      {(checkPermission: CheckPermissionFunction) =>
        <div className={'col-12'}>
          <Panel title={'work.attr.keywords'} toggleable={true}>
            <MultiInputControls<WorkKeywordDTO>
              canEdit={checkPermission().canWrite}
              renderLines={(keywordDto: WorkKeywordDTO, onKeywordChange: (keyword: WorkKeywordDTO) => void, customActions: Actions, idx: number, lines: WorkKeywordDTO[], onChangeAll: (newValues: WorkKeywordDTO[], changedValues: WorkKeywordDTO[], originals?: WorkKeywordDTO[]) => void): React.ReactNode => {
                const isNew = (
                  props.enrichedValues?.workKeywords as WorkKeywordDTO[] | undefined
                )?.some(
                  (enrichedKeyword: WorkKeywordDTO): boolean =>
                    enrichedKeyword.keyword === keywordDto.keyword
                    && enrichedKeyword.id === keywordDto.id
                ) ?? false;
                return (
                  <InputGroup
                    delete={{
                      action: customActions.delete,
                      disabled: !checkPermission().canWrite,
                    }}
                  >
                    <LabelledInputText
                      id={`keyword${idx}`}
                      autoFocus={props.autoFocus}
                      value={keywordDto.keyword}
                      permissions={checkPermission('keyword')}
                      validation={props.validation.root?.fields.work_keywords?.children[idx]?.fields?.keyword}
                      onChange={(keyword: string): void => onKeywordChange({...keywordDto, keyword})}
                      label={'work.attr.keyword'}
                      isNew={isNew}

                    />
                  </InputGroup>
                );
              }}
              lines={props.modifiedModel.workKeywords || []}
              onChange={(keywords: WorkKeywordDTO[]): void => props.setField('workKeywords', keywords)}
              addNew={(rows: WorkKeywordDTO[]): number => rows.push(getDefaultWorkKeyword())}
            />
          </Panel>
        </div>
      }
    </PermissionGate>
    <PermissionGate entity={'work_generic'} field={'work_subjects'}>
      {(checkPermission: CheckPermissionFunction) =>
        <div className={'col-12'}>
          <Subjects
            autoFocus={props.autoFocus}
            title={'work.attr.subjects'}
            checkPermission={checkPermission}
            subjects={props.modifiedModel.workSubjects||[]}
            onChange={(subjects: SubjectDTO[]): void => props.setField('workSubjects', subjects)}
            validation={props.validation.root?.fields.work_subjects}
            typeSubset={props.pubtypeGroup === 'patent' ? 'patent' : 'generic'}
            type={'work'}
            enrichedValues={props.enrichedValues}
          />
        </div>
      }
    </PermissionGate>
    <PermissionGate entity={'work_generic'} field={'work_descriptions'}>
      {(checkPermission: CheckPermissionFunction) =>
        <div className={'col-12'}>
          <Panel title={'work.attr.descriptions'} toggleable={true}>
            <MultiInputControls<WorkDescriptionDTO>
              canEdit={checkPermission().canWrite}
              lines={props.modifiedModel.workDescriptions||[]}
              onChange={(descriptions: WorkDescriptionDTO[]): void => props.setField('workDescriptions', descriptions)}
              addNew={(rows: WorkDescriptionDTO[]): number => rows.push(getDefaultWorkDescription())}
              renderLines={(description: WorkDescriptionDTO, onDescriptionChange: (workDescriptionDTO: WorkDescriptionDTO) => void, customActions: Actions, idx: number): ReactNode => {
                const isNew = (
                  props.enrichedValues?.workDescriptions as WorkDescriptionDTO[] | undefined
                )?.some(
                  (enrichedDescription: WorkDescriptionDTO): boolean =>
                    enrichedDescription.content === description.content
                    && enrichedDescription.descriptionType === description.descriptionType
                    && enrichedDescription.language === description.language
                ) ?? false;
                return (
                  <div className={'flex flex-column gap-4'}>
                    <div className={'w-12 p-fluid'}>
                      <LabelledInputTextarea
                        id={`description${idx}content`}
                        autoFocus={props.autoFocus}
                        value={description.content}
                        rows={5}
                        permissions={checkPermission('content')}
                        validation={props.validation.root?.fields.work_descriptions?.children[idx]?.fields?.content}
                        onChange={(content: string): void => onDescriptionChange({...description, content})}
                        label={'work.attr.description.attr.description'}
                        isNew={isNew}
                      />
                    </div>
                    <div className={'flex flex-column gap-4 md:flex-row gap-2'}>
                      <div className={'w-12 md:w-6'}>
                        <LabelledDropdown
                          className={'w-12'}
                          options={getVocabularyOptions(intl.locale, 'language_list', props.pubtypeGroup)}
                          id={`description${idx}language`}
                          value={description.language}
                          permissions={checkPermission('language')}
                          validation={props.validation.root?.fields.work_descriptions?.children[idx]?.fields?.language}
                          onChange={(language: string): void => onDescriptionChange({...description, language})}
                          label={'work.attr.description.attr.language'}
                          isNew={isNew}
                        />
                      </div>
                      <div className={'w-12 md:w-6'}>
                        <LabelledDropdown
                          className={'w-12'}
                          options={getVocabularyOptions(intl.locale, 'description_types_list', props.pubtypeGroup)}
                          id={`description${idx}type`}
                          value={description.descriptionType}
                          permissions={checkPermission('description_type')}
                          validation={props.validation.root?.fields.work_descriptions?.children[idx]?.fields?.description_type}
                          onChange={(descriptionType: string): void => onDescriptionChange({
                            ...description,
                            descriptionType,
                          })}
                          label={'work.attr.description.attr.type'}
                          isNew={isNew}
                      />
                      </div>
                    </div>
                    <JustifiedDeleteButton onClick={customActions.delete} disabled={!checkPermission().canWrite}/>
                  </div>
                );
              }}
            />
          </Panel>
        </div>
      }
    </PermissionGate>
  </Block>;
};
